import type { ReactNode } from 'react';
// EAA-1837: `Popover` comes straight from antd because the UI kit barrel
// (`src/components/ui/index.ts`) does not re-export it — the same import the
// sibling `AddRoleCombobox` already uses in this folder.
import { Popover } from 'antd';
import { Button } from '@/components/ui';

export type RoleMapFilterMenu = 'status' | 'apps' | 'scope' | 'columns';

/**
 * Which glyph the trigger carries, and on which side — the export is not
 * uniform: "Tình trạng"/"Ứng dụng" lead with a ⊕, "Cột" trails a columns icon,
 * and "Phạm vi" trails a chevron with no leading glyph at all.
 */
type FilterButtonIcon = 'add-leading' | 'columns-trailing' | 'chevron-trailing';

interface Props {
    label: string;
    /** Selected values rendered on the button face — the export shows the
     *  "Ứng dụng" filter carrying `lms` / `ach` inline. */
    chips?: string[];
    /** Chips are monospace for identifiers (app codes) and plain for prose
     *  labels ("Bình thường"), which would read as broken in a mono face. */
    monoChips?: boolean;
    /** Value rendered inline in ink after a muted label ("Phạm vi: Mặc định"). */
    value?: string;
    /** Dashed + muted while unset, solid + ink once set (the export's two states). */
    active: boolean;
    icon: FilterButtonIcon;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    children: ReactNode;
}

function AddIcon({ active }: { active: boolean }) {
    return (
        <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
            className={active ? 'rm-filter-btn__icon is-on' : 'rm-filter-btn__icon'}
        >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v8M8 12h8" strokeLinecap="round" />
        </svg>
    );
}

function ColumnsIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="15"
            height="15"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            aria-hidden="true"
            className="rm-filter-btn__icon"
        >
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M9 4v16" />
        </svg>
    );
}

function ChevronIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
            className="rm-filter-btn__icon"
        >
            <path d="m6 9 6 6 6-6" strokeLinecap="round" />
        </svg>
    );
}

/**
 * EAA-1837: one toolbar filter/menu trigger from `role-map-table.html` — a 36px
 * pill that switches from a dashed muted outline (nothing selected) to a solid
 * inked outline with the selected values as inline chips. Shared by "Tình
 * trạng", "Ứng dụng", "Phạm vi" and "Cột" so the four cannot drift apart.
 *
 * `open` is CONTROLLED from the view model rather than held locally, because the
 * column headers' funnel icons are a second trigger for the same two menus (the
 * export puts "Lọc theo ứng dụng" / "Lọc theo tình trạng" in the `<th>`s) — a
 * separate popover per trigger would give the operator two different filters
 * wearing one name.
 */
export function RoleMapFilterButton({
    label,
    chips,
    monoChips = true,
    value,
    active,
    icon,
    open,
    onOpenChange,
    children,
}: Props) {
    return (
        <Popover
            trigger="click"
            open={open}
            onOpenChange={onOpenChange}
            content={children}
            placement="bottomLeft"
            overlayInnerStyle={{ padding: 0 }}
        >
            <button
                type="button"
                className={active ? 'rm-filter-btn is-active' : 'rm-filter-btn'}
                aria-haspopup="true"
                aria-expanded={open}
            >
                {icon === 'add-leading' && <AddIcon active={active} />}
                <span
                    className={
                        value
                            ? 'rm-filter-btn__label rm-filter-btn__label--muted'
                            : 'rm-filter-btn__label'
                    }
                >
                    {label}
                </span>
                {value && <span className="rm-filter-btn__value">{value}</span>}
                {chips && chips.length > 0 && (
                    <>
                        <span className="rm-filter-btn__divider" />
                        {chips.map((chip) => (
                            <span
                                className={
                                    monoChips
                                        ? 'rm-filter-btn__chip is-mono'
                                        : 'rm-filter-btn__chip'
                                }
                                key={chip}
                            >
                                {chip}
                            </span>
                        ))}
                    </>
                )}
                {icon === 'columns-trailing' && <ColumnsIcon />}
                {icon === 'chevron-trailing' && <ChevronIcon />}
            </button>
        </Popover>
    );
}

/** EAA-1837: the option row shared by the "Tình trạng"/"Ứng dụng"/"Cột"/"Phạm
 *  vi" and bulk-scope menus — a teal check when on, an equal-width spacer when
 *  off so the labels never shift. `pinned` renders the export's tinted, inert
 *  entry (a column that cannot be hidden, or a scope that is already current
 *  and would therefore be a no-op to "apply"). */
export function RoleMapMenuOption({
    checked,
    label,
    hint,
    pinned,
    title,
    onToggle,
}: {
    checked: boolean;
    label: string;
    hint?: string;
    pinned?: boolean;
    title?: string;
    onToggle: () => void;
}) {
    // EAA-1817: raw <button> → kit Button — this is a plain menu-item toggle,
    // not part of the Popover trigger's shell logic above, so it is in scope
    // for the sweep while the trigger itself is not.
    return (
        <Button
            className={
                'rm-menu-option' + (checked ? ' is-checked' : '') + (pinned ? ' is-pinned' : '')
            }
            onClick={pinned ? undefined : onToggle}
            aria-pressed={checked}
            disabled={pinned}
            title={title}
        >
            {checked ? (
                <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.4}
                    aria-hidden="true"
                    className="rm-menu-option__check"
                >
                    <path d="m5 13 4 4L19 7" strokeLinecap="round" />
                </svg>
            ) : (
                <span className="rm-menu-option__spacer" aria-hidden="true" />
            )}
            <span className="rm-menu-option__label">{label}</span>
            {hint && <span className="rm-menu-option__hint">{hint}</span>}
        </Button>
    );
}
